import React, { useCallback, useEffect, useState } from 'react';
import { Loader2, ShieldAlert, Trash2, UserPlus } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Iter52.82 · Intentos que el anti-bots cortó antes de enviar el código.
 * Si uno parece una persona real (el caso del autorrelleno del navegador del
 * 19/10), se pasa a lead; si es un bot, se borra.
 */
const LeadsBloqueados = ({ authAxios, puedeBorrar = true }) => {
  const [data, setData] = useState({ total: 0, items: [], descartados_por_volumen_hoy: 0 });
  const [loading, setLoading] = useState(true);
  const [seleccion, setSeleccion] = useState([]);
  const [trabajando, setTrabajando] = useState(null);
  const [page, setPage] = useState(0);
  const PAGE_SIZE = 50;

  const cargar = useCallback(async () => {
    setLoading(true);
    try {
      const { data: d } = await authAxios.get(`${API}/leads-bloqueados`, {
        params: { skip: page * PAGE_SIZE, limit: PAGE_SIZE },
      });
      setData(d);
      setSeleccion([]);
    } catch (e) {
      toast.error('No se han podido cargar los intentos bloqueados');
    }
    setLoading(false);
  }, [authAxios, page]);

  useEffect(() => { cargar(); }, [cargar]);

  const pasarALead = async (id) => {
    setTrabajando(id);
    try {
      const { data: r } = await authAxios.post(`${API}/leads-bloqueados/${id}/pasar-a-lead`);
      toast.success(`Pasado a lead: ${r.email}`);
      cargar();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'No se ha podido pasar a lead');
    }
    setTrabajando(null);
  };

  const borrarSeleccion = async () => {
    if (!seleccion.length) return;
    if (!window.confirm(`Se van a borrar ${seleccion.length} intento(s) bloqueado(s). Esta acción no se puede deshacer. ¿Continuar?`)) return;
    setTrabajando('borrado');
    try {
      const { data: r } = await authAxios.post(`${API}/leads-bloqueados/borrar`, { ids: seleccion });
      toast.success(`${r.borrados} intento(s) borrado(s)`);
      cargar();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'No se han podido borrar');
    }
    setTrabajando(null);
  };

  const todos = data.items.length > 0 && seleccion.length === data.items.length;
  const alternarTodos = () => setSeleccion(todos ? [] : data.items.map(x => x.id));
  const alternar = (id) => setSeleccion(s => s.includes(id) ? s.filter(x => x !== id) : [...s, id]);

  return (
    <div className="bg-white shadow-sm border border-orange-500/20 rounded-2xl p-6" data-testid="panel-leads-bloqueados">
      <div className="flex justify-between items-center mb-4 gap-3 flex-wrap">
        <div>
          <h2 className="text-2xl font-black flex items-center gap-3">
            <ShieldAlert className="w-6 h-6 text-amber-500" /> Bloqueados por anti-bots
          </h2>
          <p className="text-slate-600 text-sm mt-1" data-testid="ayuda-bloqueados">
            Intentos que el anti-bots cortó. Si uno parece una persona real, pásalo a lead. Si es un bot, bórralo.
          </p>
        </div>
        <div className="flex items-center gap-2 text-sm">
          <span className="px-3 py-1 bg-amber-500/20 text-amber-700 rounded-full" data-testid="bloqueados-total">{data.total} total</span>
          {puedeBorrar && (
            <button
              onClick={borrarSeleccion}
              disabled={!seleccion.length || trabajando === 'borrado'}
              data-testid="btn-borrar-bloqueados"
              className="px-3 py-1.5 rounded-lg bg-rose-600 hover:bg-rose-700 text-white text-xs font-semibold disabled:opacity-40 flex items-center gap-2"
            >
              {trabajando === 'borrado' ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Trash2 className="w-3.5 h-3.5" />}
              Borrar seleccionados{seleccion.length ? ` (${seleccion.length})` : ''}
            </button>
          )}
        </div>
      </div>

      {data.descartados_por_volumen_hoy > 0 && (
        <p className="text-xs text-slate-500 mb-3" data-testid="bloqueados-descartados">
          Hoy se han descartado {data.descartados_por_volumen_hoy} intentos adicionales por exceso de volumen desde una misma IP (no se guardan para no llenar la base).
        </p>
      )}

      {loading ? (
        <div className="py-10 text-center text-slate-400"><Loader2 className="w-6 h-6 animate-spin mx-auto" /></div>
      ) : data.items.length === 0 ? (
        <p className="py-10 text-center text-slate-500" data-testid="bloqueados-vacio">No hay ningún intento bloqueado.</p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-xs uppercase text-slate-500 border-b">
                {puedeBorrar && <th className="py-2 pr-2"><input type="checkbox" checked={todos} onChange={alternarTodos} data-testid="chk-bloqueados-todos" /></th>}
                <th className="py-2 pr-3">Fecha</th>
                <th className="py-2 pr-3">Email</th>
                <th className="py-2 pr-3">Teléfono</th>
                <th className="py-2 pr-3">Empresa</th>
                <th className="py-2 pr-3">Campos rellenados</th>
                <th className="py-2 pr-3">Valor de la trampa</th>
                <th className="py-2 pr-3">Navegador</th>
                <th className="py-2"></th>
              </tr>
            </thead>
            <tbody>
              {data.items.map(x => (
                <tr key={x.id} className="border-b border-slate-100" data-testid={`fila-bloqueado-${x.id}`}>
                  {puedeBorrar && <td className="py-2 pr-2"><input type="checkbox" checked={seleccion.includes(x.id)} onChange={() => alternar(x.id)} data-testid={`chk-bloqueado-${x.id}`} /></td>}
                  <td className="py-2 pr-3 whitespace-nowrap">{String(x.created_at || '').slice(0, 16).replace('T', ' ')}</td>
                  <td className="py-2 pr-3">{x.email || <span className="text-slate-400">—</span>}</td>
                  <td className="py-2 pr-3">{x.telefono || <span className="text-slate-400">—</span>}</td>
                  <td className="py-2 pr-3">{x.empresa || <span className="text-slate-400">—</span>}</td>
                  <td className="py-2 pr-3 text-xs text-slate-500">{(x.campos_rellenados || []).join(', ')}</td>
                  <td className="py-2 pr-3 font-mono text-xs">{x.valor_trampa || '—'}</td>
                  <td className="py-2 pr-3 text-xs text-slate-400 max-w-[220px] truncate" title={x.user_agent}>{x.user_agent || '—'}</td>
                  <td className="py-2">
                    <button
                      onClick={() => pasarALead(x.id)}
                      disabled={trabajando === x.id}
                      data-testid={`btn-pasar-a-lead-${x.id}`}
                      className="px-2.5 py-1 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-semibold disabled:opacity-40 flex items-center gap-1.5 whitespace-nowrap"
                    >
                      {trabajando === x.id ? <Loader2 className="w-3 h-3 animate-spin" /> : <UserPlus className="w-3 h-3" />}
                      Pasar a lead
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {data.total > PAGE_SIZE && (
            <div className="flex items-center justify-between mt-4 text-sm">
              <button className="px-3 py-1.5 rounded-lg border disabled:opacity-40" disabled={page === 0} onClick={() => setPage(p => p - 1)} data-testid="bloqueados-anterior">Anterior</button>
              <span className="text-slate-500">{page * PAGE_SIZE + 1}–{Math.min((page + 1) * PAGE_SIZE, data.total)} de {data.total}</span>
              <button className="px-3 py-1.5 rounded-lg border disabled:opacity-40" disabled={(page + 1) * PAGE_SIZE >= data.total} onClick={() => setPage(p => p + 1)} data-testid="bloqueados-siguiente">Siguiente</button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default LeadsBloqueados;
